import { supabase } from "@/lib/supabase";
import { fetchSquadById } from "@/lib/squad";
import { dismissLeftSquad } from "@/lib/leftSquads";
import {
  clearPendingInviteDecline,
  markPendingInviteDecline,
} from "@/lib/squadLeaveSync";

export type PendingSquadInvite = {
  squad_id: string;
  squad_name: string;
  invited_at: string;
};

export type KickedMemberForReinvite = {
  user_id: string;
  user_name: string;
  avatar_url?: string;
  removed_at: string;
  member_status: string | null;
};

export type AcceptedSquadInvite = {
  squadId: string;
  squadName: string;
};

export type SquadInviteErrorCode =
  | "invite_not_found"
  | "forbidden"
  | "not_authenticated"
  | "unknown";

export class SquadInviteError extends Error {
  readonly code: SquadInviteErrorCode;

  constructor(code: SquadInviteErrorCode, message?: string) {
    const defaults: Record<SquadInviteErrorCode, string> = {
      invite_not_found: "Einladung nicht gefunden.",
      forbidden: "Keine Berechtigung.",
      not_authenticated: "Nicht angemeldet.",
      unknown: "Aktion fehlgeschlagen.",
    };
    super(message ?? defaults[code]);
    this.name = "SquadInviteError";
    this.code = code;
  }
}

function parseInviteRpcCode(message: string): SquadInviteErrorCode {
  const lower = message.toLowerCase();
  if (lower.includes("invite_not_found")) return "invite_not_found";
  if (lower.includes("forbidden")) return "forbidden";
  if (lower.includes("not_authenticated")) return "not_authenticated";
  return "unknown";
}

export const squadKickedMembersForReinviteKey = (squadId: string) =>
  ["squad-kicked-members-for-reinvite", squadId] as const;

export async function reinviteSquadMember(
  squadId: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase.rpc("reinvite_squad_member", {
    p_squad_id: squadId,
    p_user_id: userId,
  });

  if (error) {
    throw new SquadInviteError(parseInviteRpcCode(error.message));
  }
}

export async function acceptSquadInvite(squadId: string): Promise<AcceptedSquadInvite> {
  const { error } = await supabase.rpc("accept_squad_invite", {
    p_squad_id: squadId,
  });

  if (error) {
    throw new SquadInviteError(parseInviteRpcCode(error.message));
  }

  const squad = await fetchSquadById(squadId);
  return {
    squadId,
    squadName: squad?.name ?? "Squad",
  };
}

export async function declineSquadInvite(squadId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw new SquadInviteError("not_authenticated");
  }

  markPendingInviteDecline(squadId);
  try {
    const { data, error } = await supabase
      .from("squad_members")
      .delete()
      .eq("squad_id", squadId)
      .eq("user_id", user.id)
      .eq("status", "invited")
      .select("id");

    if (error) {
      throw error;
    }

    if (!data?.length) {
      throw new SquadInviteError("invite_not_found");
    }

    dismissLeftSquad(squadId);
  } finally {
    setTimeout(() => clearPendingInviteDecline(squadId), 3_000);
  }
}

export async function fetchKickedMembersForReinvite(
  squadId: string,
): Promise<KickedMemberForReinvite[]> {
  const { data, error } = await supabase.rpc("list_kicked_members_for_reinvite", {
    p_squad_id: squadId,
  });

  if (error) {
    throw new SquadInviteError(parseInviteRpcCode(error.message));
  }

  return (data ?? []).map((row) => ({
    user_id: row.user_id,
    user_name: row.user_name,
    avatar_url: row.avatar_url ?? undefined,
    removed_at: row.removed_at,
    member_status: row.member_status,
  }));
}

export async function fetchMyPendingInvites(): Promise<PendingSquadInvite[]> {
  const { data, error } = await supabase.rpc("get_my_pending_squad_invites");

  if (error) {
    throw new SquadInviteError(parseInviteRpcCode(error.message));
  }

  return (data ?? []).map((row) => ({
    squad_id: row.squad_id,
    squad_name: row.squad_name,
    invited_at: row.invited_at,
  }));
}
